import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import { api } from '@/shared/api/client';
import type { Schema } from '@/shared/api/contract';
import { ApiError } from '@/shared/api/errors';
import { decimalFor } from '@/shared/utils/money';

import { quoteDetailKey, quoteListsKey } from './api';

/**
 * «Añadir» from the tariffs (contract v10.52, ADR 0046 of the back). **The basket is a
 * draft**: there is no cart apart from the quote, so what the search adds goes straight
 * into one of the person's drafts and the editor of always opens it. Here and not in a
 * feature because two features add to it: the search of «Documentos» (and its catalogs)
 * and the quote editor («Pegar lista»).
 */
export type BasketDraft = Schema<'QuoteBasketDraft'>;
export type QuoteBasket = Schema<'QuoteBasket'>;
export type TariffLineAdd = Schema<'TariffLineAdd'>;
type Quote = Schema<'QuoteRead'>;

/**
 * Under the lists on purpose: every write of the editor invalidates `quoteListsKey`, and
 * the basket is a list of drafts with their number of lines and their total, which those
 * writes change. One prefix, so no write can forget it.
 */
export const basketKey = [...quoteListsKey, 'basket'] as const;

export const basketApi = {
  get: (): Promise<QuoteBasket> => api.get<QuoteBasket>('/api/quotes/basket'),
  /** A draft from the tariffs. For a profile without `quotes` the back makes it one
   * whatever it is asked. */
  create: (): Promise<Quote> => api.post<Quote>('/api/quotes', { origin: 'tariff' }),
  /** At the end of the draft, in this order. 409 when an article's tariff was replaced
   * between the search and the click: the back says to search it again. */
  addLines: (quoteId: string, items: TariffLineAdd[]): Promise<Quote> =>
    api.post<Quote>(`/api/quotes/${quoteId}/tariff-lines`, { items }),
};

// --------------------------------------------------------------------------- //
// Which draft «Añadir» goes to
// --------------------------------------------------------------------------- //

/** The value of «Uno nuevo»: the next «Añadir» makes a draft. */
export const NEW_DRAFT = 'nuevo';
const CHOSEN_KEY = 'cubas.borrador';
const listeners = new Set<() => void>();
let chosen = readChosen();

/**
 * This browser's choice, kept in `localStorage` so a reload in the middle of a list does
 * not send the next line elsewhere. A per-person convenience and nothing more: a choice
 * that is not among the person's drafts (another person on this computer, a draft since
 * approved) is ignored, and a storage that refuses only means it is not remembered.
 */
function readChosen(): string | null {
  try {
    return globalThis.localStorage?.getItem(CHOSEN_KEY) ?? null;
  } catch {
    return null;
  }
}

export function setChosenDraft(value: string | null): void {
  chosen = value;
  try {
    if (value) globalThis.localStorage?.setItem(CHOSEN_KEY, value);
    else globalThis.localStorage?.removeItem(CHOSEN_KEY);
  } catch {
    // Kept in memory for this tab; see above.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useChosenDraft(): string | null {
  return useSyncExternalStore(
    subscribe,
    () => chosen,
    () => null,
  );
}

/**
 * The draft «Añadir» goes to: the chosen one while it is still an open draft of the
 * person's, else the tariff draft touched most recently. A draft of a supplier's offer
 * (Dirección has those too) only when chosen: the search does not fill one by default.
 * None with «Uno nuevo», or with no tariff draft: the first «Añadir» makes one.
 */
export function targetDraft(
  drafts: readonly BasketDraft[],
  choice: string | null,
): BasketDraft | null {
  if (choice === NEW_DRAFT) return null;
  return (
    drafts.find((draft) => draft.id === choice) ??
    drafts.find((draft) => draft.origin !== 'offer') ??
    null
  );
}

// --------------------------------------------------------------------------- //
// Hooks
// --------------------------------------------------------------------------- //

export function useBasket(enabled: boolean) {
  return useQuery<QuoteBasket, ApiError>({
    queryKey: basketKey,
    queryFn: basketApi.get,
    enabled,
  });
}

/** The draft made by the first «Añadir», shared while it is on its way: two quick clicks
 * on two articles with no draft yet make one draft, not two. */
let creating: Promise<string> | null = null;

function makeDraft(queryClient: QueryClient): Promise<string> {
  creating ??= basketApi
    .create()
    .then((quote) => {
      queryClient.setQueryData(quoteDetailKey(quote.id), quote);
      // Into the basket at once: the next «Añadir» reads it before the refetch lands.
      queryClient.setQueryData<QuoteBasket>(basketKey, (old) => ({
        drafts: [
          {
            id: quote.id,
            origin: quote.origin ?? 'tariff',
            lines_count: quote.lines?.length ?? 0,
            total_net: quote.total_net,
            client_id: quote.client_id ?? null,
            client_name: null,
            created_at: quote.created_at,
            updated_at: quote.created_at,
          },
          ...(old?.drafts ?? []).filter((draft) => draft.id !== quote.id),
        ],
      }));
      setChosenDraft(quote.id);
      return quote.id;
    })
    .finally(() => {
      creating = null;
    });
  return creating;
}

/**
 * «Añadir»: the articles at the end of the draft this person is filling, made first when
 * there is none. Answers with the whole quote, which seeds its editor.
 */
export function useAddToQuote() {
  const queryClient = useQueryClient();
  return useMutation<Quote, ApiError, TariffLineAdd[]>({
    mutationFn: async (items) => {
      const basket = await queryClient.fetchQuery({
        queryKey: basketKey,
        queryFn: basketApi.get,
        staleTime: 30_000,
      });
      const target = targetDraft(basket.drafts ?? [], chosen)?.id ?? (await makeDraft(queryClient));
      try {
        return await basketApi.addLines(target, items);
      } catch (error) {
        // The draft may have been approved, cancelled or deleted in another tab: ask the
        // basket again, so the bar and the next «Añadir» go elsewhere, and forget a choice
        // that is no longer a draft. A 409 for a replaced tariff leaves the draft as it is.
        if (error instanceof ApiError && (error.status === 404 || error.status === 409)) {
          const fresh = await queryClient
            .fetchQuery({ queryKey: basketKey, queryFn: basketApi.get, staleTime: 0 })
            .catch(() => null);
          const gone = fresh !== null && !(fresh.drafts ?? []).some((d) => d.id === target);
          if (gone && chosen === target) setChosenDraft(null);
        }
        throw error;
      }
    },
    onSuccess: (quote) => {
      queryClient.setQueryData(quoteDetailKey(quote.id), quote);
      void queryClient.invalidateQueries({ queryKey: quoteListsKey });
    },
  });
}

/** The lines of a list, at the end of a draft the editor already has open. */
export function useAddTariffLines(quoteId: string) {
  const queryClient = useQueryClient();
  return useMutation<Quote, ApiError, TariffLineAdd[]>({
    mutationFn: (items) => basketApi.addLines(quoteId, items),
    onSuccess: (quote) => {
      queryClient.setQueryData(quoteDetailKey(quote.id), quote);
      void queryClient.invalidateQueries({ queryKey: quoteListsKey });
    },
  });
}

/**
 * A quantity typed for «Añadir», as the API takes it (`2,5` → `2.5`), or null when it is
 * not one: empty, zero, negative or not a number. Only read, never computed with.
 */
export function quantityFor(typed: string): string | null {
  const value = decimalFor(typed);
  const parsed = Number(value);
  return value !== '' && Number.isFinite(parsed) && parsed > 0 ? value : null;
}
